import React, { useState } from 'react';
import { alpha, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import dayjs from 'dayjs';
import { sxConfig } from './workspace.style';
import { DataGridIdentifiers, DatagridPremiumUi } from '../../Interface';
import { dataGridPremiumColumns, getContrastColor } from '../../../utils/helper';
import workspaceService from '../../../service/CRM/workspace.service';
import colorFinder from '../../../utils/colorFinder';
import { isExpired, isToday } from './workspaceUtils';

const OverviewPage = ({ setSelectedProjectId, workspaceState }) => {
  const { autoCompleteOptions } = workspaceState;
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'taskEndDate', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getList = (gridState) => {
    workspaceService.getOverviewData(gridState).then((data) => {
      setReports(data);
    });
  };

  const columns = dataGridPremiumColumns([
    {
      field: 'title',
      sortable: false,
      headerName: 'Adatlap teendőkkel',
      type: 'string',
      renderCell: (data) => {
        const expired = isExpired(data.row.taskEndDate);

        return (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {expired && <ErrorIcon sx={{ color: 'error.main' }} />}
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>{data.row.projectName}</Typography>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>{data.row.simpleTaskName}</Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'projectStatusId',
      sortable: false,
      headerName: 'Státusz',
      type: 'singleSelect',
      valueOptions: autoCompleteOptions.status,
      renderCell: (data) => {
        const statusColor = data.row.projectStatusColor;
        const color = statusColor ? colorFinder(statusColor)?.colorCode ?? statusColor : 'transparent';

        return (
          <Stack justifyContent="center" width="fit-content" height="100%">
            <Chip
              label={data.row.projectStatus}
              size="small"
              sx={{
                background: color,
                color: getContrastColor(color),
                fontWeight: 'bold',
              }}
            />
          </Stack>
        );
      },
    },
    {
      field: 'taskRespPersonName',
      sortable: false,
      headerName: 'Teendő felelőse',
      type: 'string',
      renderCell: (data) => {
        return (
          <Stack justifyContent="center" height="100%">
            <Typography sx={{ fontSize: '14px' }}>{data.row.taskRespPersonName}</Typography>
          </Stack>
        );
      },
    },
    {
      field: 'taskEndDate',
      headerName: 'Határidő',
      type: 'date',
      valueGetter: (_data, row) => {
        return new Date(row.taskEndDate);
      },
      renderCell: (data) => {
        const expired = isExpired(data.row.taskEndDate);
        const dueToday = isToday(data.row.taskEndDate);

        return (
          <Stack justifyContent="center" height="100%">
            <Typography
              sx={{
                fontSize: '14px',
                color: expired ? 'error.main' : dueToday ? 'primary.main' : '#000',
                fontWeight: 'bold',
              }}
            >
              {dueToday ? 'Ma' : dayjs(data.row.taskEndDate).format('YYYY.MM.DD')}
            </Typography>
          </Stack>
        );
      },
    },
  ]);

  return (
    <Paper sx={sxConfig.card}>
      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.WorkspaceOverviewReports}
        datagridInitialState={initialState}
        dataLoader={getList}
        rows={reports.rows}
        rowCount={reports.rowCount}
        columns={columns}
        paginationAndfilterMode="server"
        getRowId={(row) => row.projectId}
        getRowHeight={() => 'auto'}
        slots={{
          toolbar: null,
        }}
        onRowClick={(rowData) => {
          setSelectedProjectId(rowData.row.projectId);
        }}
      />
    </Paper>
  );
};

export default OverviewPage;
